import type { CalendarComponent, WorkspaceSettings } from '../types/workspace';

export type CalendarParts = Record<string, string | number | null>;
export type CalendarConfig = WorkspaceSettings['calendar'];
export const defaultCalendar: CalendarConfig = {
  name: 'Standardkalender', yearLabel: 'Jahr', periodLabel: 'Periode', dayLabel: 'Tag', periodsPerYear: 12, daysPerPeriod: null,
  components: [
    { key: 'day', label: 'Tag', kind: 'number', values: [], sortPriority: 3, separator: '.', showInShort: true, role: 'day' },
    { key: 'period', label: 'Periode', kind: 'number', values: [], sortPriority: 2, separator: '.', showInShort: true, role: 'period' },
    { key: 'year', label: 'Jahr', kind: 'number', values: [], sortPriority: 1, separator: '', showInShort: true, role: 'cycle' }
  ],
  origin: {}, originEarth: null,
  clock: { hoursPerDay: 24, minutesPerHour: 60, secondsPerMinute: 60, earthSecondsPerDay: 86_400, showSeconds: false },
  conversion: { anchorEarth: null, anchorCalendarParts: {}, anchorClock: null, cycleEarthSeconds: null, validFromEarth: null }
};

export function effectiveCalendarParts(value: { calendarParts?: CalendarParts; calendarDay?: number | null; calendarPeriod?: number | null; calendarYear?: number | null }): CalendarParts {
  if (value.calendarParts && Object.values(value.calendarParts).some((part) => part !== null && part !== '')) return value.calendarParts;
  return {
    ...(value.calendarDay !== null && value.calendarDay !== undefined ? { day: value.calendarDay } : {}),
    ...(value.calendarPeriod !== null && value.calendarPeriod !== undefined ? { period: value.calendarPeriod } : {}),
    ...(value.calendarYear !== null && value.calendarYear !== undefined ? { year: value.calendarYear } : {})
  };
}

export function hasCalendarParts(parts?: CalendarParts | null) {
  return Boolean(parts && Object.values(parts).some((value) => value !== null && value !== ''));
}

export function calendarWeekday(config: CalendarConfig, position: number | null) {
  const names = config.week?.dayNames;
  if (position === null || !Number.isFinite(position) || !names?.length) return null;
  return names[((Math.floor(position) % names.length) + names.length) % names.length] ?? null;
}

export function formatCalendarDate(config: CalendarConfig, value: { calendarParts?: CalendarParts; calendarDay?: number | null; calendarPeriod?: number | null; calendarYear?: number | null; clockLabel?: string | null }, short = false) {
  const parts = effectiveCalendarParts(value);
  const visible = config.components.filter((component) => !short || component.showInShort);
  let label = '';
  for (const component of visible) {
    const raw = parts[component.key];
    if (raw === null || raw === undefined || raw === '') continue;
    label += `${String(raw)}${component.separator}`;
  }
  label = label.trim();
  if (!label) return '';
  return value.clockLabel ? `${label} · ${value.clockLabel}` : label;
}

function comparePart(component: CalendarComponent, left: unknown, right: unknown) {
  const leftEmpty = left === null || left === undefined || left === ''; const rightEmpty = right === null || right === undefined || right === '';
  if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1;
  if (component.kind === 'number') return Number(left) - Number(right);
  if (component.kind === 'ordered') {
    const leftIndex = component.values.indexOf(String(left)); const rightIndex = component.values.indexOf(String(right));
    return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
  }
  return String(left).localeCompare(String(right), 'de-DE', { numeric: true });
}

export function compareCalendarDates(config: CalendarConfig, left: { calendarParts?: CalendarParts; calendarDay?: number | null; calendarPeriod?: number | null; calendarYear?: number | null }, right: { calendarParts?: CalendarParts; calendarDay?: number | null; calendarPeriod?: number | null; calendarYear?: number | null }) {
  const leftParts = effectiveCalendarParts(left); const rightParts = effectiveCalendarParts(right);
  for (const component of [...config.components].filter((item) => item.sortPriority !== null).sort((a, b) => a.sortPriority! - b.sortPriority!)) {
    const compared = comparePart(component, leftParts[component.key], rightParts[component.key]);
    if (compared) return compared;
  }
  return 0;
}
